import { LitElement, html, css, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { dispatch } from '../../utils/event-helpers';
import { resolveFileIcon, resolveFileIconColor, formatBytes } from '../../utils/file-icon-map';

export type FileStatus = 'uploading' | 'complete' | 'failed';

/**
 * A single-file progress card rendered inside an `asc-file-upload` widget.
 *
 * Displays the file's resolved type icon, name, size or upload progress, a status
 * indicator, a progress bar, and action controls (delete and, on failure, retry).
 * The implementer drives all state externally via the `status` and `progress` properties;
 * the component is purely presentational.
 *
 * > **Usage:** Designed to be composed exclusively by `asc-file-upload`. Do not use
 * > standalone in application code.
 *
 * @element asc-file-upload-item
 *
 * @fires asc-file-upload-remove - Fired when the delete button is activated. Detail: `{ file: File }`
 * @fires asc-file-upload-retry  - Fired when the "Try again" button is activated (status="failed"). Detail: `{ file: File }`
 *
 * @csspart base           - Root `<div>` — full card surface (border, background, padding, layout).
 * @csspart file-icon      - Icon container `<span>` — the resolved file-type SVG icon.
 * @csspart name           - `<span>` containing the file name text.
 * @csspart size           - `<span>` containing the size / progress label.
 * @csspart status         - `<span>` containing the status indicator (spinner/checkmark/x + label).
 * @csspart progress-track - `<div>` — the full-width progress bar track.
 * @csspart progress-fill  - `<div>` — the filled portion of the progress bar.
 * @csspart error-message  - `<p>` — detailed error description shown when status="failed".
 * @csspart retry-button   - `<button>` — the "Try again" button shown when status="failed".
 * @csspart delete-button  - `<button>` — the trash/delete icon button (always visible).
 *
 * @cssprop --asc-file-upload-item-bg                  - Card background color. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-file-upload-item-border-color         - Default card border color. Default: `var(--asc-border-secondary)`
 * @cssprop --asc-file-upload-item-border-width         - Default card border width. Default: `var(--asc-border-width)`
 * @cssprop --asc-file-upload-item-border-radius        - Card corner radius. Default: `var(--asc-radius-sm)`
 * @cssprop --asc-file-upload-item-padding              - Uniform padding on all four sides. Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-file-upload-item-content-gap          - Gap between file icon and text+progress group. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-file-upload-item-text-gap             - Gap between name row and progress bar row. Default: `var(--asc-spacing-xs)`
 * @cssprop --asc-file-upload-item-supporting-gap       - Gap between size label, divider, and status region. Default: `var(--asc-spacing-md)`
 * @cssprop --asc-file-upload-item-border-color-failed  - Border color when status="failed". Default: `var(--asc-border-error)`
 * @cssprop --asc-file-upload-item-name-color           - File name text color. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-file-upload-item-size-color           - Size/progress label color. Default: `var(--asc-text-tertiary)`
 * @cssprop --asc-file-upload-item-status-icon-size     - Status icon size. Default: `16px`
 * @cssprop --asc-file-upload-item-status-complete-color - "Complete" label + icon color. Default: `var(--asc-text-success-primary)`
 * @cssprop --asc-file-upload-item-status-uploading-color - "Uploading" label + icon color. Default: `var(--asc-text-quaternary)`
 * @cssprop --asc-file-upload-item-status-failed-color  - "Failed" label + icon color. Default: `var(--asc-text-error-primary)`
 * @cssprop --asc-file-upload-item-retry-color          - "Try again" button text color. Default: `var(--asc-text-error-primary)`
 * @cssprop --asc-file-upload-item-progress-height      - Progress bar height. Default: `8px`
 * @cssprop --asc-file-upload-item-progress-track-bg    - Progress track background. Default: `var(--asc-bg-secondary)`
 * @cssprop --asc-file-upload-item-progress-fill        - Progress fill color. Default: `var(--asc-fg-brand-primary)`
 * @cssprop --asc-file-upload-item-progress-pct-color   - Percentage label color. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-file-upload-item-progress-gap         - Gap between progress bar and percentage label. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-file-upload-item-delete-padding       - Padding of the delete icon button. Default: `var(--asc-spacing-sm)`
 * @cssprop --asc-file-upload-item-delete-radius        - Border radius of the delete button hit area. Default: `var(--asc-radius-sm)`
 * @cssprop --asc-file-upload-item-delete-icon-size     - Size of the trash icon inside delete button. Default: `16px`
 */
@customElement('asc-file-upload-item')
export class AscFileUploadItem extends LitElement {
  static override styles = css`
    :host {
      display: block;
    }

    /* ═══════════════════════════════════════════════════════════
       BASE CARD
       ═══════════════════════════════════════════════════════════ */
    .base {
      /* Named container so @container queries below target this element
         specifically and not an ancestor container. */
      container-type: inline-size;
      container-name: file-upload-item;
      position: relative;
      display: flex;
      align-items: flex-start;
      padding: var(--asc-file-upload-item-padding, var(--asc-spacing-xl, 16px));
      /* Reserve right-side clearance for the absolute delete button:
         button right offset (7px) + button width (6px pad + 16px icon + 6px pad = 28px) +
         5px breathing room = 40px. Overrides the shorthand above. */
      padding-right: 40px;
      background: var(--asc-file-upload-item-bg, var(--asc-bg-primary, #fff));
      border: var(--asc-file-upload-item-border-width, var(--asc-border-width, 1px))
        solid
        var(--asc-file-upload-item-border-color, var(--asc-border-secondary, #a5b8bf));
      border-radius: var(--asc-file-upload-item-border-radius, var(--asc-radius-sm, 8px));
      box-sizing: border-box;
      width: 100%;
    }

    .base--failed {
      border-color: var(
        --asc-file-upload-item-border-color-failed,
        var(--asc-border-error, #d2260f)
      );
      border-width: 2px;
    }

    /* ═══════════════════════════════════════════════════════════
       CONTENT AREA (icon + text group)
       ═══════════════════════════════════════════════════════════ */
    .content {
      display: flex;
      flex: 1 0 0;
      min-width: 0;
      gap: var(--asc-file-upload-item-content-gap, var(--asc-spacing-lg, 12px));
      align-items: flex-start;
    }

    /* ═══════════════════════════════════════════════════════════
       FILE TYPE ICON
       ═══════════════════════════════════════════════════════════ */
    .file-icon {
      position: relative;
      flex-shrink: 0;
      width: 40px;
      height: 40px;
    }

    .file-icon__page {
      position: absolute;
      /* Page background leaves 17.5% left margin and 2.5% right margin */
      inset: 0 2.5% 0 17.5%;
    }

    /* Generic page shape rendered as an SVG-based CSS shape */
    .file-icon__page svg {
      display: block;
      width: 100%;
      height: 100%;
    }

    .file-icon__badge {
      position: absolute;
      inset: 45% 32.5% 15% 2.5%;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 2px;
      padding: 2px 3px;
      font-family: var(--asc-font-family-body);
      font-size: 8px;
      font-weight: var(--asc-font-weight-bold, 700);
      color: var(--asc-fg-white, #fff);
      line-height: 1;
      white-space: nowrap;
    }

    /* ═══════════════════════════════════════════════════════════
       TEXT GROUP
       ═══════════════════════════════════════════════════════════ */
    .text-group {
      display: flex;
      flex: 1 0 0;
      min-width: 0;
      flex-direction: column;
      gap: var(--asc-file-upload-item-text-gap, var(--asc-spacing-xs, 4px));
    }

    .text-group--failed {
      gap: var(--asc-spacing-sm, 6px);
    }

    /* Figma: inner "Text and supporting text" frame has 2px (spacing-xxs) gap
       between the file name and the supporting text row, distinct from the 4px
       gap between this block and the progress bar row. */
    .text-and-supporting {
      display: flex;
      flex-direction: column;
      gap: var(--asc-spacing-xxs, 2px);
      align-items: flex-start;
      width: 100%;
    }

    .file-name {
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-sm, 14px);
      font-weight: var(--asc-font-weight-medium, 500);
      line-height: var(--asc-line-height-text-sm, 20px);
      color: var(--asc-file-upload-item-name-color, var(--asc-text-secondary, #30383b));
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      /* Parent is a column-flex with align-items:flex-start, which collapses
         children to intrinsic content width — defeating overflow:hidden.
         width:100% pins it to the container so ellipsis truncation works. */
      width: 100%;
      margin: 0;
    }

    /* ═══════════════════════════════════════════════════════════
       SUPPORTING TEXT ROW (size + divider + status)
       ═══════════════════════════════════════════════════════════ */
    .supporting-row {
      display: flex;
      align-items: center;
      gap: var(--asc-file-upload-item-supporting-gap, var(--asc-spacing-md, 8px));
      width: 100%;
    }

    .file-size {
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-sm, 14px);
      font-weight: var(--asc-font-weight-regular, 400);
      line-height: var(--asc-line-height-text-sm, 20px);
      color: var(--asc-file-upload-item-size-color, var(--asc-text-tertiary, #49565a));
      white-space: nowrap;
      flex-shrink: 0;
      margin: 0;
    }

    .divider {
      width: 1px;
      height: 12px;
      background: var(--asc-border-secondary, #a5b8bf);
      flex-shrink: 0;
    }

    .status-region {
      display: flex;
      align-items: center;
      gap: var(--asc-spacing-xs, 4px);
      flex-shrink: 0;
    }

    .status-icon {
      width: var(--asc-file-upload-item-status-icon-size, 16px);
      height: var(--asc-file-upload-item-status-icon-size, 16px);
      flex-shrink: 0;
    }

    .status-label {
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-sm, 14px);
      font-weight: var(--asc-font-weight-medium, 500);
      line-height: var(--asc-line-height-text-sm, 20px);
      white-space: nowrap;
      margin: 0;
    }

    .status-label--complete {
      color: var(
        --asc-file-upload-item-status-complete-color,
        var(--asc-text-success-primary, #064307)
      );
    }
    .status-icon--complete {
      color: var(
        --asc-file-upload-item-status-complete-color,
        var(--asc-text-success-primary, #064307)
      );
    }

    .status-label--uploading {
      color: var(
        --asc-file-upload-item-status-uploading-color,
        var(--asc-text-quaternary, #627278)
      );
    }
    .status-icon--uploading {
      color: var(
        --asc-file-upload-item-status-uploading-color,
        var(--asc-text-quaternary, #627278)
      );
    }

    .status-label--failed {
      color: var(
        --asc-file-upload-item-status-failed-color,
        var(--asc-text-error-primary, #d2260f)
      );
    }
    .status-icon--failed {
      color: var(
        --asc-file-upload-item-status-failed-color,
        var(--asc-text-error-primary, #d2260f)
      );
    }

    /* ═══════════════════════════════════════════════════════════
       PROGRESS BAR
       ═══════════════════════════════════════════════════════════ */
    .progress-row {
      display: flex;
      align-items: center;
      gap: var(--asc-file-upload-item-progress-gap, var(--asc-spacing-lg, 12px));
      width: 100%;
    }

    .progress-track {
      flex: 1 0 0;
      position: relative;
      height: var(--asc-file-upload-item-progress-height, 8px);
      border-radius: var(--asc-radius-full, 9999px);
      background: var(
        --asc-file-upload-item-progress-track-bg,
        var(--asc-bg-secondary, #e6eaec)
      );
      overflow: hidden;
    }

    .progress-fill {
      position: absolute;
      inset-block: 0;
      left: 0;
      border-radius: var(--asc-radius-full, 9999px);
      background: var(
        --asc-file-upload-item-progress-fill,
        var(--asc-fg-brand-primary, #008577)
      );
      transition: width var(--asc-transition-base);
    }

    .progress-pct {
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-sm, 14px);
      font-weight: var(--asc-font-weight-medium, 500);
      line-height: var(--asc-line-height-text-sm, 20px);
      color: var(--asc-file-upload-item-progress-pct-color, var(--asc-text-secondary, #30383b));
      white-space: nowrap;
      flex-shrink: 0;
      margin: 0;
    }

    /* ═══════════════════════════════════════════════════════════
       ERROR MESSAGE + RETRY
       ═══════════════════════════════════════════════════════════ */
    .error-message {
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-sm, 14px);
      font-weight: var(--asc-font-weight-regular, 400);
      line-height: var(--asc-line-height-text-sm, 20px);
      color: var(
        --asc-file-upload-item-status-failed-color,
        var(--asc-text-error-primary, #d2260f)
      );
      margin: 0;
    }

    .retry-button {
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-sm, 14px);
      font-weight: var(--asc-font-weight-semibold, 600);
      line-height: var(--asc-line-height-text-sm, 20px);
      color: var(--asc-file-upload-item-retry-color, var(--asc-text-error-primary, #d2260f));
      text-align: left;
    }

    .retry-button:focus-visible {
      outline: var(--asc-focus-ring-width, 2px) solid var(--asc-focus-ring, #009e86);
      outline-offset: var(--asc-focus-ring-offset, 2px);
      border-radius: var(--asc-radius-xxs, 2px);
    }

    /* ═══════════════════════════════════════════════════════════
       DELETE BUTTON
       ═══════════════════════════════════════════════════════════ */
    .delete-button {
      position: absolute;
      top: 7px;
      right: 7px;
      background: none;
      border: none;
      padding: var(--asc-file-upload-item-delete-padding, var(--asc-spacing-sm, 6px));
      cursor: pointer;
      border-radius: var(--asc-file-upload-item-delete-radius, var(--asc-radius-sm, 8px));
      color: var(--asc-fg-secondary, #49565a);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .base--failed .delete-button {
      /* 2px border offsets content box → pull button 1px inward on each side */
      top: 6px;
      right: 6px;
    }

    .delete-button:hover {
      background: var(--asc-bg-primary_hover, #e6eaec);
      color: var(--asc-fg-primary, #30383b);
    }

    .delete-button:focus-visible {
      outline: var(--asc-focus-ring-width, 2px) solid var(--asc-focus-ring, #009e86);
      outline-offset: var(--asc-focus-ring-offset, 2px);
    }

    /* ═══════════════════════════════════════════════════════════
       NARROW CARD — stack status below size, hide divider
       Threshold: 320px card width. At this width the text-group is
       ~208px (320 - 16px left pad - 40px right pad - 40px icon - 12px gap)
       which is not wide enough to show both the size label and the
       status region side-by-side without truncating either.
       ═══════════════════════════════════════════════════════════ */
    @container file-upload-item (max-width: 320px) {
      .supporting-row {
        flex-direction: column;
        align-items: flex-start;
        gap: var(--asc-spacing-xxs, 2px);
      }

      .divider {
        display: none;
      }
    }

    .delete-button svg {
      display: block;
      width: var(--asc-file-upload-item-delete-icon-size, 16px);
      height: var(--asc-file-upload-item-delete-icon-size, 16px);
    }
  `;

  /** The native File object for this queue entry. Required. Not reflected as an attribute. */
  @property({ type: Object }) file!: File;

  /** Current upload state. Drives the visual treatment of the card. */
  @property({ type: String, reflect: true }) status: FileStatus = 'uploading';

  /** Upload progress percentage (0–100). Required when status is 'uploading'. */
  @property({ type: Number }) progress = 0;

  /** Failure reason shown when status is 'failed'. Falls back to a generic message if empty. */
  @property({ type: String, attribute: 'error-message' }) errorMessage = '';

  /** When true, the remove and retry buttons are disabled and cannot be activated. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  @state() private _ariaLabel = '';

  override willUpdate(changed: PropertyValues<this>) {
    if (changed.has('file') || changed.has('status')) {
      const name = this.file?.name ?? 'File';
      this._ariaLabel = `${name} — ${this.status}`;
    }
  }

  private _handleDelete() {
    dispatch<{ file: File }>(this, 'asc-file-upload-remove', { detail: { file: this.file } });
  }

  private _handleRetry() {
    dispatch<{ file: File }>(this, 'asc-file-upload-retry', { detail: { file: this.file } });
  }

  private _renderFileIcon() {
    const iconType = this.file ? resolveFileIcon(this.file.name) : 'FILE';
    const badgeColor = resolveFileIconColor(iconType);

    return html`
      <span part="file-icon" class="file-icon" aria-hidden="true">
        <span class="file-icon__page">
          <!-- Generic document page shape matching Figma: white body, light stroke, gray fold -->
          <svg viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <!--
              Page body: white fill with translucent border.
              Stroke color rgba(0,0,0,0.12) — no-token: matches Figma exactly;
              no alpha token at this precise opacity.
            -->
            <path
              d="M2 0h20l10 10v28a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2z"
              fill="#fff"
              stroke="rgba(0,0,0,0.12)"
              stroke-width="1"
            />
            <!--
              Fold triangle: #F2F4F7 — no-token: mid-point between bg-secondary-subtle
              (#F7F9F9) and bg-secondary (#E6EAEC); used only for this decorative icon.
            -->
            <path d="M22 0 32 10H24a2 2 0 0 1-2-2V0z" fill="#F2F4F7" />
          </svg>
        </span>
        <span class="file-icon__badge" style="background: ${badgeColor};">
          ${iconType}
        </span>
      </span>
    `;
  }

  private _renderStatus() {
    if (this.status === 'complete') {
      return html`
        <span part="status" class="status-region" aria-live="polite">
          <!-- Check-circle icon -->
          <svg
            class="status-icon status-icon--complete"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M8 1.33334C4.32001 1.33334 1.33334 4.32001 1.33334 8.00001C1.33334 11.68 4.32001 14.6667 8 14.6667C11.68 14.6667 14.6667 11.68 14.6667 8.00001C14.6667 4.32001 11.68 1.33334 8 1.33334ZM6.66668 11.3333L3.33334 8.00001L4.27334 7.06001L6.66668 9.44668L11.7267 4.38668L12.6667 5.33334L6.66668 11.3333Z"
              fill="currentColor"
            />
          </svg>
          <span class="status-label status-label--complete">Complete</span>
        </span>
      `;
    }

    if (this.status === 'uploading') {
      return html`
        <span part="status" class="status-region" aria-live="polite">
          <!-- Upload icon -->
          <svg
            class="status-icon status-icon--uploading"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M2 11.3333V12.6667C2 13.4 2.6 14 3.33333 14H12.6667C13.4 14 14 13.4 14 12.6667V11.3333C14 10.9667 13.7 10.6667 13.3333 10.6667C12.9667 10.6667 12.6667 10.9667 12.6667 11.3333V12.6667H3.33333V11.3333C3.33333 10.9667 3.03333 10.6667 2.66667 10.6667C2.3 10.6667 2 10.9667 2 11.3333ZM5.52667 5.86001L7.33333 4.05334V10C7.33333 10.3667 7.63333 10.6667 8 10.6667C8.36667 10.6667 8.66667 10.3667 8.66667 10V4.05334L10.4733 5.86001C10.7333 6.12001 11.1533 6.12001 11.4133 5.86001C11.6733 5.60001 11.6733 5.18001 11.4133 4.92001L8.47333 1.98001C8.21333 1.72001 7.79333 1.72001 7.53333 1.98001L4.58667 4.92001C4.32667 5.18001 4.32667 5.60001 4.58667 5.86001C4.84667 6.12001 5.26667 6.12001 5.52667 5.86001Z"
              fill="currentColor"
            />
          </svg>
          <span class="status-label status-label--uploading">Uploading...</span>
        </span>
      `;
    }

    // failed
    return html`
      <span part="status" class="status-region" aria-live="polite">
        <!-- X icon -->
        <svg
          class="status-icon status-icon--failed"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M8 1.33334C4.32 1.33334 1.33333 4.32001 1.33333 8.00001C1.33333 11.68 4.32 14.6667 8 14.6667C11.68 14.6667 14.6667 11.68 14.6667 8.00001C14.6667 4.32001 11.68 1.33334 8 1.33334ZM11.3333 10.3933L10.3933 11.3333L8 8.93999L5.60667 11.3333L4.66667 10.3933L7.06 7.99999L4.66667 5.60666L5.60667 4.66666L8 7.05999L10.3933 4.66666L11.3333 5.60666L8.94 7.99999L11.3333 10.3933Z"
            fill="currentColor"
          />
        </svg>
        <span class="status-label status-label--failed">Failed</span>
      </span>
    `;
  }

  override render() {
    const filename = this.file?.name ?? '';
    const totalBytes = this.file?.size ?? 0;
    const clampedProgress =
      this.status === 'complete' ? 100 : Math.min(Math.max(this.progress, 0), 100);
    const uploadedBytes = Math.round((clampedProgress / 100) * totalBytes);

    const sizeLabel =
      this.status === 'uploading'
        ? `${formatBytes(uploadedBytes)} of ${formatBytes(totalBytes)}`
        : formatBytes(totalBytes);

    return html`
      <div
        part="base"
        role="group"
        class="base ${this.status === 'failed' ? 'base--failed' : ''}"
        aria-label=${this._ariaLabel}
      >
        <div class="content">
          ${this._renderFileIcon()}
          <div class="text-group ${this.status === 'failed' ? 'text-group--failed' : ''}">
            <!-- Name + supporting text (2px inner gap per Figma) -->
            <div class="text-and-supporting">
              <p part="name" class="file-name">${filename}</p>

              <!-- Supporting text row: size + divider + status -->
              <div class="supporting-row">
                <p part="size" class="file-size">${sizeLabel}</p>
                <span class="divider" aria-hidden="true"></span>
                ${this._renderStatus()}
              </div>
            </div>

            <!-- Progress bar (hidden when failed) -->
            ${this.status !== 'failed'
              ? html`
                  <div class="progress-row">
                    <div
                      part="progress-track"
                      class="progress-track"
                      role="progressbar"
                      aria-valuenow=${clampedProgress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                      aria-label="Upload progress for ${filename}"
                    >
                      <div
                        part="progress-fill"
                        class="progress-fill"
                        style="width: ${clampedProgress}%;"
                      ></div>
                    </div>
                    <p class="progress-pct">${clampedProgress}%</p>
                  </div>
                `
              : html`
                  <!-- Error message + retry -->
                  <p part="error-message" class="error-message" role="alert">
                    ${this.errorMessage || 'Upload failed. Please try again.'}
                  </p>
                  <button
                    part="retry-button"
                    type="button"
                    class="retry-button"
                    aria-label="Retry upload for ${filename}"
                    ?disabled=${this.disabled}
                    @click=${this._handleRetry}
                  >
                    Try again
                  </button>
                `}
          </div>
        </div>

        <!-- Delete button (always visible) -->
        <button
          part="delete-button"
          type="button"
          class="delete-button"
          aria-label="Remove ${filename}"
          ?disabled=${this.disabled}
          @click=${this._handleDelete}
        >
          <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path
              d="M4.66667 13.3333C4.66667 14.0667 5.26667 14.6667 6 14.6667H10C10.7333 14.6667 11.3333 14.0667 11.3333 13.3333V5.33333H4.66667V13.3333ZM6 6.66667H10V13.3333H6V6.66667ZM9.66667 3.00001L9 2.33334H7L6.33333 3.00001H4V4.33334H12V3.00001H9.66667Z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-file-upload-item': AscFileUploadItem;
  }
}
